import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../api';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => useContext(SiteSettingsContext);

// Default store settings (fallbacks if API fails)
const DEFAULT_STORE_SETTINGS = {
  freeShippingAbove: 999,
  standardShipping: 49,
  expressShipping: 99,
  overnightShipping: 199,
  gstRate: 18,
  codEnabled: true,
  codFee: 40,
  codMaxAmount: 10000,
  storeName: 'Poshe Styles',
  storeEmail: 'hello@poshe-styles.com',
  storePhone: '+91 98765 43210',
  storeAddress: 'Mumbai, Maharashtra, India',
  currency: 'INR',
};

export const SiteSettingsProvider = ({ children }) => {
  const [appearance, setAppearance] = useState(null);
  const [content, setContent] = useState(null);
  const [collections, setCollections] = useState([]);
  const [storeSettings, setStoreSettings] = useState(DEFAULT_STORE_SETTINGS);
  const [loading, setLoading] = useState(true);

  const fetchSiteData = async () => {
    try {
      setLoading(true);
      // Single batch call replaces 4 separate API calls
      const { data } = await API.get('/api/public/batch');
      if (data.success) {
        if (data.appearance) setAppearance(data.appearance);
        if (data.content) setContent(data.content);
        if (data.collections) setCollections(data.collections.filter(c => c.isVisible));
        if (data.settings) setStoreSettings({ ...DEFAULT_STORE_SETTINGS, ...data.settings });
      }
    } catch (error) {
      // Fallback to individual calls if batch endpoint fails
      console.warn('Batch endpoint failed, falling back to individual calls:', error.message);
      try {
        const [appRes, contRes, colRes, settingsRes] = await Promise.all([
          API.get('/api/public/appearance').catch(() => ({ data: { success: false } })),
          API.get('/api/public/content').catch(() => ({ data: { success: false } })),
          API.get('/api/public/collections').catch(() => ({ data: { success: false } })),
          API.get('/api/public/settings').catch(() => ({ data: { success: false } })),
        ]);
        if (appRes.data.success) setAppearance(appRes.data.appearance);
        if (contRes.data.success) setContent(contRes.data.content);
        if (colRes.data.success) setCollections(colRes.data.collections.filter(c => c.isVisible));
        if (settingsRes.data.success) setStoreSettings({ ...DEFAULT_STORE_SETTINGS, ...settingsRes.data.settings });
      } catch (fallbackError) {
        console.error('Failed to load site settings:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteData();
  }, []);

  // Apply CSS Variables based on theme settings
  useEffect(() => {
    if (appearance?.theme) {
      const root = document.documentElement;
      const t = appearance.theme;
      
      if (t.primaryColor) root.style.setProperty('--primary', t.primaryColor);
      root.style.setProperty('--primary-hover', t.primaryColor + 'cc'); // 80% opacity
      if (t.buttonColor) root.style.setProperty('--secondary', t.buttonColor);
      root.style.setProperty('--secondary-hover', t.buttonColor + 'cc');
      if (t.backgroundColor) root.style.setProperty('--bg-color', t.backgroundColor);
      if (t.textColor) root.style.setProperty('--text-color', t.textColor);
    }
  }, [appearance]);

  return (
    <SiteSettingsContext.Provider value={{ 
      appearance, content, collections, storeSettings, loading, 
      refreshSettings: fetchSiteData 
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
